import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Send, Camera, X, Copy, CheckCircle2, Link as LinkIcon,
  MessageCircle, Mail, Smartphone, Paperclip, FileText, Share2, Trash2,
  Lock, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import PhotoLightbox from "@/components/PhotoLightbox";
import { TimePicker } from "@/components/TimePicker";
import ShareAction from "@/components/ShareAction";
import { useBilling } from "@/context/BillingContext";
import { useNavigate } from "react-router-dom";

// Per-plan photo limit map — must match `core/plans.TRIAL_PHOTOS_LIMIT_BY_PLAN`
// on the backend. Source of truth lives there; this mirror lets the UI
// gate the upload control immediately without an extra round-trip.
const TRIAL_PHOTOS_LIMIT_BY_PLAN = {
  free: 0,
  basic: 3,
  pro: 6,
  ai_manager: 6,
};
const MAX_PHOTOS_HARD_CAP = 6;
const MAX_DIMENSION = 900; // px
const JPEG_QUALITY = 0.82;

// Resize image client-side and return data URL
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function TrialBlock({ kind, client, clientId, onChange, onSavedTrial }) {
  // kind: 'makeup' | 'hair'
  const sheet = client[kind] || {};
  const trial = sheet.trial || { date: "", time: "", location: "", completed: false, photos: [], files: [] };
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  // Plan-aware photo cap for trial photos. PRO/AI_MANAGER = 6, BASIC = 3,
  // FREE = 0 (locked). Same map mirrors the backend in `core/plans.py`.
  //
  // P0 fix: while billing is still hydrating on cold start we must NOT
  // collapse to `effective_plan = "free"` — that would lock paid users out
  // of trial photo upload until they navigate to /app/plans. While loading
  // we use the hard cap (6 photos) so the UI stays open; the backend is
  // still the source of truth and will reject uploads if the real plan
  // doesn't allow them.
  const { billing, isHydrating, openUpgrade } = useBilling();
  const navigate = useNavigate();
  const effectivePlan = isHydrating ? null : (billing?.effective_plan || "free");
  const MAX_PHOTOS = isHydrating
    ? MAX_PHOTOS_HARD_CAP
    : (TRIAL_PHOTOS_LIMIT_BY_PLAN[effectivePlan] ?? 0);
  const canUploadTrialPhotos = isHydrating || MAX_PHOTOS > 0;
  const [sendOpen, setSendOpen] = useState(false);
  const [shareTrialOpen, setShareTrialOpen] = useState(false);
  const [trialToken, setTrialToken] = useState(trial.share_token || "");
  const [creatingShare, setCreatingShare] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const canWebShare = typeof navigator !== "undefined" && !!navigator.share;

  const updateTrial = (patch) => {
    const newTrial = { ...trial, ...patch };
    onChange({ ...client, [kind]: { ...sheet, trial: newTrial } });
  };

  const photos = trial.photos || [];
  const files = trial.files || [];
  const remaining = MAX_PHOTOS - photos.length;

  const onPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > remaining) {
      toast.error(`Puoi caricare al massimo ${MAX_PHOTOS} foto. Restano ${remaining} slot.`);
    }
    const toAdd = files.slice(0, remaining);
    try {
      const dataUrls = await Promise.all(toAdd.map(fileToDataUrl));
      updateTrial({ photos: [...photos, ...dataUrls] });
      toast.success(`${dataUrls.length} foto aggiunta/e (ricorda di salvare)`);
    } catch (err) {
      toast.error("Errore nel caricamento foto");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    updateTrial({ photos: photos.filter((_, i) => i !== idx) });
  };

  // ---- PDF attachments (allegati) ----
  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });

  const onPdfSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (pdfRef.current) pdfRef.current.value = "";
    if (!selected.length) return;
    const pdfs = selected.filter((f) => f.type === "application/pdf");
    if (!pdfs.length) {
      toast.error("Sono ammessi solo file PDF");
      return;
    }
    const tooBig = pdfs.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) {
      toast.error(`File "${tooBig.name}" troppo grande (max 10MB)`);
      return;
    }
    setUploadingPdf(true);
    try {
      const newFiles = [...files];
      for (const f of pdfs) {
        const dataUrl = await readFileAsDataUrl(f);
        const { data } = await api.post(`/clients/${clientId}/trial/${kind}/files`, {
          name: f.name,
          data: dataUrl,
          size: f.size,
        });
        // Sync local list with backend response (names/sizes only)
        newFiles.splice(0, newFiles.length, ...data.files.map((x) => ({ name: x.name, size: x.size })));
      }
      updateTrial({ files: newFiles });
      toast.success(`${pdfs.length} allegato/i aggiunto/i`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingPdf(false);
    }
  };

  const removeFile = async (idx) => {
    try {
      const { data } = await api.delete(`/clients/${clientId}/trial/${kind}/files/${idx}`);
      updateTrial({ files: data.files.map((x) => ({ name: x.name, size: x.size })) });
      toast.success("Allegato rimosso");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // ---- Share trial (photos + files) via public link ----
  const shareUrl = trialToken ? `${window.location.origin}/trial/${trialToken}` : "";

  const openShareTrial = async () => {
    if (!photos.length && !files.length) {
      toast.error("Aggiungi almeno una foto o un allegato prima di condividere la prova");
      return;
    }
    setCreatingShare(true);
    try {
      let token = trialToken;
      if (!token) {
        const { data } = await api.post(`/clients/${clientId}/trial/${kind}/share`);
        token = data.token;
        setTrialToken(token);
        updateTrial({ share_token: token });
      }
      setShareTrialOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreatingShare(false);
    }
  };

  const getTrialShareMessage = () => {
    const link = `${window.location.origin}/trial/${trialToken}`;
    return (
`Buongiorno ${client.name || ""},

Le invio le foto e i dettagli della prova.

Può visualizzare tutto da questo link:
${link}

Resto a disposizione.

---

Good morning ${client.name || ""},

Here you can find the photos and details of your trial:
${link}

Please let me know if you need anything.`
    );
  };

  const trialShareSubject = kind === "makeup"
    ? "Prova trucco · Makeup trial"
    : "Prova capelli · Hair trial";

  const copyTrialLink = async () => {
    setBusyAction("copy-link");
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato negli appunti");
    } catch { toast.error("Impossibile copiare automaticamente"); }
    finally { setBusyAction(""); }
  };

  const copyTrialMessage = async () => {
    setBusyAction("copy-msg");
    try {
      await navigator.clipboard.writeText(getTrialShareMessage());
      toast.success("Messaggio copiato negli appunti");
    } catch { toast.error("Impossibile copiare automaticamente"); }
    finally { setBusyAction(""); }
  };

  const shareTrialWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(getTrialShareMessage())}`, "_blank", "noopener");
  };

  const shareTrialEmail = async () => {
    // Branded server-side delivery via Resend — replaces the legacy
    // mailto: handler. Mirrors the quote/contract flow: real email send,
    // CTA-rich template, end-to-end tracking via the communications
    // collection. The button stays visible in the share dialog so the
    // operator's UX is unchanged.
    if (!client?.email) {
      toast.error("Aggiungi l'email del cliente prima di poter inviare");
      return;
    }
    setBusyAction("email-trial");
    try {
      const { data } = await api.post(`/clients/${clientId}/trial/${kind}/email`, {
        app_origin: window.location.origin,
      });
      toast.success(`Email inviata a ${data?.to || client.email}`);
      setShareTrialOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Errore invio email");
    } finally {
      setBusyAction("");
    }
  };

  const shareTrialNative = async () => {
    if (!canWebShare) return;
    setBusyAction("system-trial");
    try {
      await navigator.share({ title: trialShareSubject, text: getTrialShareMessage(), url: shareUrl });
    } catch {/* dismissed */}
    finally { setBusyAction(""); }
  };

  const sendAppointment = async () => {
    if (!trial.date || !trial.time) {
      toast.error("Imposta data e orario della prova");
      return;
    }
    setSending(true);
    try {
      // Persist the trial fields atomically so the pro doesn't need to click "Salva"
      const { data } = await api.post(`/clients/${clientId}/trial/${kind}/send`, {
        date: trial.date,
        time: trial.time,
        location: trial.location || "",
      });
      // Backend returns a message for legacy paths — we ignore it here and build a
      // bilingual, richer message on the frontend (so we can match the brief exactly).
      onSavedTrial(kind, data.trial);
      setSendOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const kindLabel = kind === "makeup" ? "trucco" : "capelli";
  const kindLabelEn = kind === "makeup" ? "makeup" : "hair";

  // Bilingual (IT + EN) appointment message.
  // Location is skipped entirely when empty / generic ("Casa") to avoid a useless Maps link.
  const getAppointmentMessage = () => {
    const rawLocation = (trial.location || "").trim();
    const genericLocations = ["casa", "home", "a casa", ""];
    const isValidForMaps = rawLocation && !genericLocations.includes(rawLocation.toLowerCase());
    const locationLine = rawLocation || "—";
    const mapsUrl = isValidForMaps
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rawLocation)}`
      : "";
    const mapsBlockIt = mapsUrl ? `\n\nApri su Maps:\n${mapsUrl}` : "";
    const mapsBlockEn = mapsUrl ? `\n\nOpen in Maps:\n${mapsUrl}` : "";
    return (
`Buongiorno ${client.name || ""},

Le confermo l'appuntamento per la prova ${kindLabel}:

Data: ${trial.date}
Orario: ${trial.time}
Location: ${locationLine}${mapsBlockIt}

Resto a disposizione.

---

Good morning ${client.name || ""},

I confirm your ${kindLabelEn} trial appointment:

Date: ${trial.date}
Time: ${trial.time}
Location: ${locationLine}${mapsBlockEn}`
    );
  };

  const appointmentMessage = sendOpen ? getAppointmentMessage() : "";
  const emailSubject = kind === "makeup"
    ? "Conferma appuntamento prova trucco · Makeup trial confirmation"
    : "Conferma appuntamento prova capelli · Hair trial confirmation";

  const copyMessage = async () => {
    setBusyAction("copy");
    try {
      await navigator.clipboard.writeText(appointmentMessage);
      toast.success("Messaggio copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare automaticamente");
    } finally { setBusyAction(""); }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(appointmentMessage)}`, "_blank", "noopener");
  };

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(appointmentMessage)}`;
  };

  const shareNative = async () => {
    if (!canWebShare) return;
    setBusyAction("system");
    try {
      await navigator.share({ title: emailSubject, text: appointmentMessage });
    } catch {/* dismissed */}
    finally { setBusyAction(""); }
  };

  return (
    <div className="border-t border-border pt-6 space-y-5" data-testid={`trial-block-${kind}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif-heading text-xl">Prova {kindLabel}</h3>
          <p className="text-xs text-neutral-500 mt-1">Pianifica e documenta la prova preliminare prima del giorno dell'evento.</p>
        </div>
        <div className="flex items-center gap-3">
          {trial.completed && (
            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs label-uppercase">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Completata
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            onClick={sendAppointment}
            disabled={sending}
            data-testid={`trial-send-${kind}-button`}
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {sending ? "Invio…" : "Invia appuntamento"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="label-uppercase">Data prova</Label>
          <Input
            type="date"
            value={trial.date || ""}
            onChange={(e) => updateTrial({ date: e.target.value })}
            className="mt-1 rounded-sm"
            data-testid={`trial-${kind}-date`}
          />
        </div>
        <div>
          <Label className="label-uppercase">Orario prova</Label>
          <TimePicker
            value={trial.time || ""}
            onChange={(v) => updateTrial({ time: v || "" })}
            testid={`trial-${kind}-time`}
          />
        </div>
        <div>
          <Label className="label-uppercase">Location prova</Label>
          <Input
            value={trial.location || ""}
            onChange={(e) => updateTrial({ location: e.target.value })}
            className="mt-1 rounded-sm"
            placeholder="Es. studio, casa sposa, hotel"
            data-testid={`trial-${kind}-location`}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id={`trial-completed-${kind}`}
          checked={!!trial.completed}
          onCheckedChange={(v) => updateTrial({ completed: !!v })}
          data-testid={`trial-${kind}-completed-toggle`}
        />
        <Label htmlFor={`trial-completed-${kind}`} className="label-uppercase cursor-pointer">Prova completata</Label>
      </div>

      {trial.sent_at && (
        <div className="text-xs text-neutral-500">
          Appuntamento inviato il {new Date(trial.sent_at).toLocaleString("it-IT")}
        </div>
      )}

      {/* Photos — gated to PRO / AI MANAGER. FREE / BASIC users see the
          upgrade card with a single, focused CTA. Existing photos (if
          any, from a downgrade) stay viewable so the user can still
          send / delete them but no new ones can be added. */}
      <div>
        <Label
          className="label-uppercase"
          data-testid={`trial-${kind}-photos-counter`}
        >
          Foto prova caricate{" "}
          <span className="text-neutral-900 font-medium">{photos.length}/{Math.max(MAX_PHOTOS, photos.length)}</span>
          {MAX_PHOTOS > 0 && photos.length < MAX_PHOTOS && (
            <span className="text-neutral-400 ml-1">(max {MAX_PHOTOS})</span>
          )}
        </Label>

        {!canUploadTrialPhotos ? (
          <div
            className="mt-2 bg-gradient-to-br from-[#FBF6EC] to-[#F5EBD7] border-2 border-primary/40 rounded-sm p-5 sm:p-6"
            data-testid={`trial-${kind}-photos-upgrade`}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-primary/15 ring-1 ring-primary/30 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif-heading text-lg text-neutral-900 flex items-center gap-2">
                  Foto prova bloccate
                  <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                </h3>
                <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">
                  Il salvataggio delle foto della prova è disponibile a partire
                  dal piano <span className="font-medium">BASIC</span> (fino a 3 foto).
                  I piani <span className="font-medium">PRO</span> e{" "}
                  <span className="font-medium">AI MANAGER™</span> permettono di salvarne fino a 6.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 w-full sm:w-auto"
                onClick={() => {
                  openUpgrade && openUpgrade({ reason: "trial_photos", required_plan: "basic" });
                  navigate("/app/plans");
                }}
                data-testid={`trial-${kind}-photos-upgrade-cta`}
              >
                Passa a BASIC
              </Button>
            </div>
            {/* Read-only preview if there are existing photos (legacy
                upload from a previous PRO period). User can still
                remove them — their data, their call. */}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-5">
                {photos.map((src, i) => (
                  <div key={i} className="relative aspect-square border border-border rounded-sm overflow-hidden bg-secondary group" data-testid={`trial-${kind}-photo-${i}`}>
                    <img
                      src={src}
                      alt={`Prova ${kindLabel} ${i + 1}`}
                      loading="lazy"
                      onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                      className="w-full h-full object-cover cursor-zoom-in"
                      data-testid={`trial-${kind}-photo-img-${i}`}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                      className="absolute top-1.5 right-1.5 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-destructive opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                      data-testid={`trial-${kind}-photo-remove-${i}`}
                      title="Rimuovi"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {photos.map((src, i) => (
                <div key={i} className="relative aspect-square border border-border rounded-sm overflow-hidden bg-secondary group" data-testid={`trial-${kind}-photo-${i}`}>
                  <img
                    src={src}
                    alt={`Prova ${kindLabel} ${i + 1}`}
                    loading="lazy"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                    className="w-full h-full object-cover cursor-zoom-in transition-transform duration-200 group-hover:scale-[1.02]"
                    data-testid={`trial-${kind}-photo-img-${i}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                    className="absolute top-1.5 right-1.5 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-destructive opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    data-testid={`trial-${kind}-photo-remove-${i}`}
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
              {Array.from({ length: Math.max(0, MAX_PHOTOS - photos.length) }).map((_, i) => (
                <button
                  key={`empty-${i}`}
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="aspect-square border border-dashed border-border rounded-sm flex flex-col items-center justify-center text-neutral-400 hover:border-primary hover:text-primary transition-colors"
                  data-testid={`trial-${kind}-photo-add-${i}`}
                >
                  <Camera className="w-6 h-6" strokeWidth={1.5} />
                  <span className="text-xs mt-1">Aggiungi foto</span>
                </button>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onPhotoSelect}
              className="hidden"
              data-testid={`trial-${kind}-photo-input`}
            />
          </>
        )}
      </div>

      {/* PDF Attachments (allegati) */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label className="label-uppercase">Allegati ({files.length})</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => pdfRef.current?.click()}
              disabled={uploadingPdf}
              data-testid={`trial-${kind}-add-files-button`}
            >
              <Paperclip className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {uploadingPdf ? "Carico…" : "Aggiungi allegati"}
            </Button>
            <Button
              type="button"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
              size="sm"
              onClick={openShareTrial}
              disabled={creatingShare}
              data-testid={`trial-${kind}-share-trial-button`}
            >
              <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {creatingShare ? "Attendere…" : "Condividi prova"}
            </Button>
          </div>
        </div>
        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid={`trial-${kind}-files-list`}>
            {files.map((f, i) => (
              <li
                key={i}
                className="flex items-center gap-3 border border-border rounded-sm px-3 py-2 bg-white"
                data-testid={`trial-${kind}-file-${i}`}
              >
                <FileText className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-sm truncate flex-1" title={f.name}>{f.name}</span>
                {f.size > 0 && (
                  <span className="text-xs text-neutral-400 tabular-nums">{(f.size / 1024).toFixed(0)} KB</span>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-neutral-400 hover:text-destructive transition-colors"
                  data-testid={`trial-${kind}-file-remove-${i}`}
                  aria-label="Rimuovi allegato"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={pdfRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={onPdfSelect}
          className="hidden"
          data-testid={`trial-${kind}-pdf-input`}
        />
      </div>

      {/* Send appointment dialog — identical UX to "Condividi preventivo e contratto" */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid={`trial-${kind}-share-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">Condividi appuntamento prova</DialogTitle>
            <DialogDescription>
              Scegli come inviare l'appuntamento alla cliente.
            </DialogDescription>
          </DialogHeader>

          {/* Message preview */}
          <div className="bg-secondary/50 border border-border rounded-sm p-4 text-xs leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid={`trial-${kind}-message-preview`}>
            {appointmentMessage}
          </div>

          {/* Actions grid — identical visual language to ClientDetail share dialog */}
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            <ShareAction
              icon={Copy}
              label="Copia messaggio"
              hint="Incolla dove vuoi"
              busy={busyAction === "copy"}
              disabled={busyAction !== ""}
              onClick={copyMessage}
              testid={`trial-${kind}-copy-action`}
            />
            <ShareAction
              icon={MessageCircle}
              label="Invia via WhatsApp"
              hint="Apre WhatsApp con il messaggio"
              disabled={busyAction !== ""}
              onClick={shareWhatsApp}
              testid={`trial-${kind}-whatsapp-action`}
            />
            <ShareAction
              icon={Mail}
              label="Invia via Email"
              hint="Apre il client email con oggetto e testo"
              disabled={busyAction !== ""}
              onClick={shareEmail}
              testid={`trial-${kind}-email-action`}
            />
            <ShareAction
              icon={Smartphone}
              label="Condividi…"
              hint={canWebShare ? "Menu nativo (AirDrop, Mail, WhatsApp…)" : "Non disponibile su questo dispositivo"}
              busy={busyAction === "system"}
              disabled={!canWebShare || busyAction !== ""}
              onClick={shareNative}
              testid={`trial-${kind}-system-action`}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Share trial (photos + files) dialog */}
      <Dialog open={shareTrialOpen} onOpenChange={setShareTrialOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid={`trial-${kind}-share-trial-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">Condividi prova</DialogTitle>
            <DialogDescription>
              Invia alla cliente il link privato con foto e allegati.
            </DialogDescription>
          </DialogHeader>

          {/* Link preview */}
          <div
            className="bg-secondary/50 border border-border rounded-sm p-3 text-xs font-mono truncate"
            data-testid={`trial-${kind}-share-trial-url`}
            title={shareUrl}
          >
            {shareUrl}
          </div>

          {/* Message preview */}
          <div
            className="bg-secondary/50 border border-border rounded-sm p-4 text-xs leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto"
            data-testid={`trial-${kind}-share-trial-message`}
          >
            {shareTrialOpen ? getTrialShareMessage() : ""}
          </div>

          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            <ShareAction
              icon={MessageCircle}
              label="Invia via WhatsApp"
              hint="Apre WhatsApp con il messaggio"
              disabled={busyAction !== ""}
              onClick={shareTrialWhatsApp}
              testid={`trial-${kind}-share-trial-whatsapp`}
            />
            <ShareAction
              icon={Mail}
              label="Invia via Email"
              hint="Apre il client email con il link"
              disabled={busyAction !== ""}
              onClick={shareTrialEmail}
              testid={`trial-${kind}-share-trial-email`}
            />
            <ShareAction
              icon={Smartphone}
              label="Condividi…"
              hint={canWebShare ? "Menu nativo (AirDrop, Mail, WhatsApp…)" : "Non disponibile su questo dispositivo"}
              busy={busyAction === "system-trial"}
              disabled={!canWebShare || busyAction !== ""}
              onClick={shareTrialNative}
              testid={`trial-${kind}-share-trial-system`}
            />
            <ShareAction
              icon={LinkIcon}
              label="Copia link"
              hint="Incolla dove vuoi"
              busy={busyAction === "copy-link"}
              disabled={busyAction !== ""}
              onClick={copyTrialLink}
              testid={`trial-${kind}-share-trial-copy-link`}
            />
            <ShareAction
              icon={Copy}
              label="Copia messaggio"
              hint="Copia il testo completo (IT + EN)"
              busy={busyAction === "copy-msg"}
              disabled={busyAction !== ""}
              onClick={copyTrialMessage}
              testid={`trial-${kind}-share-trial-copy-msg`}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen photo viewer */}
      <PhotoLightbox
        open={lightboxOpen}
        photos={photos}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onDelete={(idx) => removePhoto(idx)}
        alt={`Prova ${kindLabel}`}
      />
    </div>
  );
}
